import { useQuery } from "@tanstack/react-query";
import dayjs, { Dayjs } from "dayjs";
import { acquireToken } from "../../api/acquireToken";
import { jepxDataProviderRequest } from "../../authConfig";
import { EXCHANGE_TIMEZONE } from "../../dayjsSetup";
import { getJepxDataProviderBaseUri } from "../../MFTEnvironmentVariables";
import { SpotPrice } from "./types";

/**
 * Formats a trading day for the `date` / `to` query parameters.
 *
 * The provider filters on the JST calendar date, because a JEPX trading day is
 * a Tokyo calendar day (48 half-hourly slots, 00:00 to 23:30 +09:00). Going
 * through `.tz()` rather than a bare `dayjs().format()` matters: near midnight
 * JST, "today" in the browser's own zone is a different — and wrong — day.
 */
export const toJstTradingDate = (date: Dayjs | string | Date): string =>
  dayjs(date).tz(EXCHANGE_TIMEZONE).format("YYYY-MM-DD");

/** The trading day currently underway in Tokyo. */
export const currentJstTradingDate = (): string => toJstTradingDate(dayjs());

/** The next Tokyo trading day — the one the day-ahead auction clears into. */
export const nextJstTradingDate = (): string =>
  toJstTradingDate(dayjs().tz(EXCHANGE_TIMEZONE).add(1, "day"));

/**
 * JEPX day-ahead spot prices for every area, over an inclusive range of trading
 * days. Omit `toDate` for a single day.
 *
 * A complete day is 48 records per area, so 432 across the nine areas. Days in
 * the range whose auction has not cleared yet are simply absent from the
 * response rather than erroring — that is how "tomorrow isn't published yet"
 * presents.
 *
 * Passes an explicit `queryFn`: the client-wide default in
 * `api/queryClient.ts` targets lazerhub and would both rewrite the URL and
 * append a `company` parameter this endpoint does not accept.
 */
export const useSpotPrices = (
  date: Dayjs | string | Date | undefined,
  toDate?: Dayjs | string | Date
) => {
  const tradingDate = date === undefined ? undefined : toJstTradingDate(date);
  const toTradingDate =
    toDate === undefined ? undefined : toJstTradingDate(toDate);

  const { data, isLoading, error } = useQuery<SpotPrice[]>({
    queryKey: ["jepx-data-provider", "spot-prices", tradingDate, toTradingDate],
    enabled: tradingDate !== undefined,
    // A cleared price never changes, so there is nothing to poll for within a
    // day. What does change is tomorrow *appearing* when its day-ahead auction
    // clears — and the desk leaves this page open across that moment, so
    // without a periodic refetch tomorrow's prices would stay blank until
    // someone happened to reload. Ten minutes is well inside the window between
    // the auction publishing and anyone needing to trade against it, at a cost
    // of six requests an hour.
    staleTime: 3 * 60 * 1000,
    refetchInterval: 3 * 60 * 1000,
    refetchOnWindowFocus: true,
    queryFn: async ({ signal }) => {
      const accessToken = await acquireToken(jepxDataProviderRequest);

      const query = new URLSearchParams({ date: tradingDate! });
      if (toTradingDate !== undefined) {
        query.set("to", toTradingDate);
      }

      const resp = await fetch(
        `${getJepxDataProviderBaseUri()}/api/spot-prices?${query.toString()}`,
        {
          headers: { Authorization: `Bearer ${accessToken}` },
          signal,
        }
      );

      if (!resp.ok) {
        // The provider returns a plain-text reason on 400/401/403, which is far
        // more useful than the bare status text.
        const detail = await resp.text().catch(() => "");
        throw new Error(
          detail
            ? `${resp.status} ${resp.statusText}: ${detail}`
            : resp.statusText
        );
      }

      return (await resp.json()) as SpotPrice[];
    },
  });

  return { spotPrices: data, loading: isLoading, error };
};
